import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'jsonHighlightDiff',
})
export class JsonHighlightDiffPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(current: any, previous?: any): SafeHtml {
    if (!current) {
      return;
    }

    const currentLines = JSON.stringify(current, null, 2).split('\n');
    const previousLines = previous ? JSON.stringify(previous, null, 2).split('\n') : [];

    const diffResult = this.lineDiff(previousLines, currentLines);

    const result: string[] = [];

    for (const item of diffResult) {
      const {line} = item;
      const {type} = item;

      let prefix = ' ';
      let bgClass = '';

      if (type === 'added') {
        prefix = '+';
        bgClass = 'bg-primary-100';
      } else if (type === 'removed') {
        prefix = '-';
        bgClass = 'bg-error-soft';
      }

      result.push(this.highlightLine(line, prefix, bgClass));
    }

    return this.sanitizer.bypassSecurityTrustHtml(
      `<div class="font-mono text-sm leading-snug">${result.join('')}</div>`,
    );
  }

  private highlightLine(line: string, prefix: string = ' ', bgClass: string = ''): string {
    const escapedLine = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const highlighted = escapedLine.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(:)?|\b(true|false|null)\b|\b[\d.]+\b)/g,
      (match) => {
        let cls = 'text-primary-400';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'text-secondary-500';
          } else {
            cls = 'text-warning';
          }
        } else if (/^(true|false)$/.test(match)) {
          cls = 'error';
        } else if (/^null$/.test(match)) {
          cls = 'success';
        }
        return `<span class="${cls}">${match}</span>`;
      },
    );

    return `<div class="${bgClass}"><span class="inline-block w-4 text-gray-500 select-none">${prefix}</span>${highlighted}</div>`;
  }

  // Diff algorithm: simple LCS-based line matcher
  private lineDiff(
    oldLines: string[],
    newLines: string[],
  ): { line: string; type: 'unchanged' | 'added' | 'removed' }[] {
    const m = oldLines.length;
    const n = newLines.length;
    const dp: number[][] = Array(m + 1)
      .fill(null)
      .map(() => Array(n + 1).fill(0));

    // Build LCS matrix
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        if (oldLines[i] === newLines[j]) {
          dp[i][j] = dp[i + 1][j + 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
      }
    }

    // Walk through matrix and build diff result
    const result: { line: string; type: 'unchanged' | 'added' | 'removed' }[] = [];
    let i = 0,
      j = 0;
    while (i < m && j < n) {
      if (oldLines[i] === newLines[j]) {
        result.push({ line: newLines[j], type: 'unchanged' });
        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        result.push({ line: oldLines[i], type: 'removed' });
        i++;
      } else {
        result.push({ line: newLines[j], type: 'added' });
        j++;
      }
    }

    // Remaining lines
    while (i < m) {
      result.push({ line: oldLines[i], type: 'removed' });
      i++;
    }
    while (j < n) {
      result.push({ line: newLines[j], type: 'added' });
      j++;
    }

    return result;
  }
}
